// The one channel of feedback that doesn't need to be looked at.
//
// A quiz answer already says right or wrong three ways — the colour on the
// button, the tick or cross, the answer row filling in — and all three are on
// the screen. Half of using this app is holding a phone somewhere other than in
// front of your face: walking, with binoculars up, or with the screen dimmed in
// a pocket. A buzz is the only one of the four that arrives anyway.
//
// Support is genuinely partial, and unusually so for a web API. `vibrate` is
// Android and desktop Chrome/Firefox; it does not exist on any iOS browser,
// including Chrome for iOS, because every one of them is WebKit and WebKit has
// never exposed the Taptic Engine to the web. So this module is written to be a
// no-op more often than not, and nothing in the app may depend on it firing.

import { getHaptics } from './prefs';

/**
 * What just happened, in the vocabulary a vibration motor has: one tap, or a
 * stutter. The wrong-answer pattern is deliberately the only one with a gap in
 * it — a single buzz is ambiguous, two is a headshake.
 */
export type Haptic = 'right' | 'wrong' | 'undo';

const PATTERNS: Record<Haptic, number | number[]> = {
  // Short enough to read as a click rather than a buzz. Anything past ~20ms on
  // an Android motor stops feeling like contact and starts feeling like an
  // alert, which is the wrong register for being right.
  right: 12,
  wrong: [16, 60, 16],
  // Lighter than either: undo is a correction, not a verdict.
  undo: 8,
};

/**
 * Buzz, if the device can and the learner hasn't turned it off.
 *
 * Deliberately silent about failure. Vibration also requires a user gesture in
 * most browsers, and every call site here is inside a click handler — but a
 * browser that disagrees throws rather than returning false, and a thrown
 * exception in an answer handler would cost the answer.
 */
export function haptic(kind: Haptic): void {
  if (!getHaptics()) return;
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(PATTERNS[kind]);
  } catch {
    /* unsupported, blocked, or no user gesture — the screen already said it */
  }
}
